'use client';

/**
 * SessionStatusOverlay — full-screen blocking modal for expired/replaced sessions.
 *
 * Renders when `sessionStatus` is 'expired' or 'replaced'.
 * Portals to `document.body` at z-index 1400 (above all modals at 1300).
 * Single "Sign In" button — no "Refresh" (if background refresh failed, user retry won't help).
 */

import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import {
  getSessionStatusSnapshot,
  subscribeSessionStatus,
} from '../sessionStatus';
import type { SessionStatus } from '../sessionStatus';
import { BRAND_BLUE } from '@voley/shared-types/constants';

const COPY: Record<Exclude<SessionStatus, 'active'>, { title: string; message: string; param: string }> = {
  expired: {
    title: 'Session Expired',
    message: 'Your session has expired. Please sign in again to continue.',
    param: 'session_expired',
  },
  replaced: {
    title: 'Signed In Elsewhere',
    message: "You've been signed in on another device. Please sign in again to continue here.",
    param: 'session_replaced',
  },
};

function handleSignIn(param: string) {
  const returnUrl = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  window.location.href = `/login?returnUrl=${returnUrl}&message=${param}`;
}

export function SessionStatusOverlay() {
  const status = useSyncExternalStore(
    subscribeSessionStatus,
    getSessionStatusSnapshot,
    // SSR snapshot — always 'active' (overlay never renders on server)
    () => 'active' as SessionStatus,
  );

  if (status === 'active') return null;

  const { title, message, param } = COPY[status];

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-card-background)',
          borderRadius: 12,
          padding: '32px 40px',
          maxWidth: 420,
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        }}
      >
        {/* Lock icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: '#FEE2E2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-error)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {title}
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
          {message}
        </p>

        <button
          onClick={() => handleSignIn(param)}
          style={{
            backgroundColor: BRAND_BLUE,
            color: 'var(--color-card-background)',
            border: 'none',
            borderRadius: 9999,
            padding: '10px 32px',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'transform 0.15s, box-shadow 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
          }}
        >
          Sign In
        </button>
      </div>
    </div>,
    document.body,
  );
}
